/*
 * @Description: 账户相关 store
 * @Author: erickuang
 * @Date: 2021-08-17 16:14:59
 * @LastEditor: erickuang
 * @LastEditTime: 2021-08-26 10:15:11
 */
import { getAccountInfo, getUserInfo, getInviteInfo, applyDeleteAccount } from '@/apis/account';
import { getUnreadMsgCount } from '@/apis/message';
import {
  UserInfo,
  AccountInfo,
  DeleteAccountPreventItem,
  AccountStatus,
  ChannelInfo,
  InviteInfo,
} from '@/types/account';
import { UnreadMessageInfo } from '@/types/message';
import { ActionContext } from 'vuex';

export interface State {
  accountInfo: AccountInfo;
  deleteAccountPreventInfos: DeleteAccountPreventItem[],
  userInfo: UserInfo;
  channelInfo: ChannelInfo;
  inviteDetail: {
    inviteInfo: InviteInfo;
    pageNo: number;
    finished: boolean;
  },
  unreadMessageInfo: UnreadMessageInfo;
}

const state: State = {
  accountInfo: {
    ticket: '',
    status: -1,
    nextApplyTimestamp: 0,
    auditMsg: '',
    type: -1,
    capability: {},
  },
  deleteAccountPreventInfos: [] as DeleteAccountPreventItem[],
  userInfo: {} as UserInfo,
  channelInfo: {
    inviter: '',
  } as ChannelInfo,
  inviteDetail: {
    inviteInfo: {} as InviteInfo,
    pageNo: 1,
    finished: false,
  },
  unreadMessageInfo: {
    loading: false,
    hasUnread: false,
  },
};

const mutations = {
  updateAccountInfo(state: State, accountInfo: AccountInfo) {
    state.accountInfo = accountInfo;
  },
  updateDeleteAccountPreventInfos(state: State, deleteAccountPreventInfos: DeleteAccountPreventItem[]) {
    state.deleteAccountPreventInfos = deleteAccountPreventInfos;
  },
  updateUserInfo(state: State, userInfo: UserInfo) {
    state.userInfo = userInfo;
  },
  updateChannelInfo(state: State, channelInfo: Partial<ChannelInfo>) {
    state.channelInfo = {
      ...state.channelInfo,
      ...channelInfo,
    };
  },
  updateInviteDetail(state: State, detail: Object) {
    state.inviteDetail = {
      ...state.inviteDetail,
      ...detail,
    };
  },
  updateInviteInfo(state: State, nextInviteInfo: InviteInfo) {
    const { inviteInfo, pageNo } = state.inviteDetail;
    const inviteInfos = pageNo === 1
      ? (nextInviteInfo?.inviteInfos || [])
      : [...(inviteInfo.inviteInfos || []), ...(nextInviteInfo?.inviteInfos || [])];
    state.inviteDetail = {
      inviteInfo: {
        ...nextInviteInfo,
        inviteInfos,
      },
      pageNo: pageNo + 1,
      finished: inviteInfos.length >= (nextInviteInfo?.useCount || 0),
    };
  },
  updateUnreadMessageInfo(state: State, unreadMessageInfo: Partial<UnreadMessageInfo>) {
    state.unreadMessageInfo = {
      ...state.unreadMessageInfo,
      ...unreadMessageInfo,
    };
  },
};

const getters = {
  ticket(state: State): string {
    return state.accountInfo.ticket;
  },
  accountStatus(state: State): AccountStatus {
    return state.accountInfo.status;
  },
  deleteAccountPreventInfos(state: State) {
    return state.deleteAccountPreventInfos;
  },
  accountAuditMsg(state: State): string {
    return state.accountInfo.auditMsg;
  },
  channelInviter(state: State): string {
    return state.channelInfo.inviter;
  },
  unreadMessageInfo(state: State) {
    return state.unreadMessageInfo;
  },
};

const actions = {
  async updateAccountInfo({ commit }: ActionContext<State, State>) {
    try {
      const result = await getAccountInfo();
      commit('updateAccountInfo', result);
      console.log('AccountInfo synced: ', result);
      return Promise.resolve(result);
    } catch (error) {
      console.error('Error. UpdateAccountInfo failed.', error);
      return Promise.reject(error);
    }
  },
  async updateDeleteAccountPreventInfos({ commit }: ActionContext<State, State>) {
    try {
      const { entities } = await applyDeleteAccount();
      commit('updateDeleteAccountPreventInfos', entities);
      console.log('updateDeleteAccountPreventInfos synced: ', entities);
      return Promise.resolve(entities);
    } catch (error) {
      console.error('Error. updateDeleteAccountPreventInfos failed.', error);
      return Promise.reject();
    }
  },
  async updateUserInfo({ commit }: ActionContext<State, State>) {
    try {
      const result = await getUserInfo();
      commit('updateUserInfo', result);
      console.log('UserInfo synced: ', result);
      return Promise.resolve(result);
    } catch (error) {
      console.error('Error. UpdateUserInfo failed.', error);
    }
  },
  async updateInviteInfo({ commit }: ActionContext<State, State>, pageNo: number) {
    try {
      const start = (pageNo - 1) * 20;
      commit('updateInviteDetail', { pageNo });
      const { entity } = await getInviteInfo({ start, length: 20 });
      entity?.code && commit('updateInviteInfo', entity);
      console.log('InviteInfo synced: ', entity);
      return Promise.resolve(entity);
    } catch (error) {
      console.error('Error. UpdateInviteInfo failed.', error);
      return Promise.reject(error);
    }
  },
  async updateUnreadMessageInfo({ state, commit }: ActionContext<State, State>) {
    if (state?.unreadMessageInfo?.loading) {
      return;
    }
    try {
      commit('updateUnreadMessageInfo', { loading: true });
      const { unreadCount = 0 } = await getUnreadMsgCount();
      commit('updateUnreadMessageInfo', { hasUnread: unreadCount > 0 });
      return Promise.resolve(unreadCount);
    } catch (error) {
      console.error('Error. updateUnreadMessageInfo failed.', error);
    } finally {
      commit('updateUnreadMessageInfo', { loading: false });
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
